import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

// Figma 54:6269 / 54:6210 — the control sits at x=0 and its label column at x=64 (toggle: 48 + 16)
// or x=40 (checkbox: 24 + 16). Revealed sub-content is a sibling of the row rather than a child of
// the label, so it has to be indented to the same x by hand. controlLabel below zeroes out MUI's
// marginLeft: -11 on the FormControlLabel root so these line up.
const TOGGLE_LABEL_OFFSET = 64;
const CHECKBOX_LABEL_OFFSET = 40;

export const styles = (theme: Theme) => {
	return ({
		iconButton: {
			padding: 4,
			marginLeft: 8
		},
		settingsIcon: {
			marginRight: 12
		},
		sectionHeading: {
			fontWeight: "bold",
			fontSize: 18,
			color: theme.palette.custom.textPrimary,
			marginBottom: 16
		},
		// Subsection headers are 16px bold (Figma "Typography/iOS/H4"), matching the Capture Forms
		// page. The Typography variant="subtitle2" these are rendered with is MUI's 14px default,
		// so the size has to be set explicitly here.
		sectionSubheading: {
			fontSize: 16,
			fontWeight: "bold",
			color: theme.palette.custom.textPrimary,
			marginBottom: 8
		},
		bodyText: {
			marginBottom: 16
		},
		// Label-above field (Figma), matching FullWidthDropdown's labelAbove: the redesign puts a
		// 14px grey label above the input instead of MUI's floating/notched label.
		labelAbove: {
			display: "block",
			marginBottom: 8,
			fontSize: 14,
			lineHeight: "20px",
			color: theme.palette.custom.textSecondary,
		},
		modalBodyBold: {
			fontWeight: "bold",
			marginTop: 16
		},
		dialogActions: {
			padding: "16px 24px"
		},
		marketLink: {
			color: theme.palette.primary.main,
			textDecoration: "none",
			"&:hover": {
				textDecoration: "underline"
			}
		},
		introText: {
			color: theme.palette.text.secondary,
			marginBottom: 8,
		},
		readOnlyList: {
			color: theme.palette.text.primary,
			marginBottom: 8,
		},
		editButton: {
			marginBottom: 4,
			textTransform: "none",
			padding: 0,
			minWidth: 0,
		},
		// marginLeft: 0 cancels MUI's -11 on the root so the control starts at the card edge and the
		// label column lands at a predictable x (see the offsets above).
		controlLabel: {
			alignItems: "flex-start",
			marginLeft: 0,
			"& .MuiFormControlLabel-label": {
				marginLeft: 0,
				marginTop: 9,
			},
		},
		controlLabelControl: {
			marginTop: 0,
			padding: 8,
		},
		// The theme's 46x26 switch geometry is tuned (thumb translateX), so leave its width alone
		// and only zero the left margin; marginRight carries the gap to the label.
		switchControl: {
			marginTop: 8,
			marginLeft: 0,
			marginRight: 18,
		},
		// 24px below the parent's description (Figma: description ends y=56, first input y=80). The
		// per-instance mt values this replaces ranged 8-24 and differed between the two tabs (MAX-3928).
		toggleSubContent: {
			marginLeft: TOGGLE_LABEL_OFFSET,
			marginTop: 24,
		},
		checkboxSubContent: {
			marginLeft: CHECKBOX_LABEL_OFFSET,
			marginTop: 24,
		},
		controlLabelDescription: {
			marginTop: 4,
		},
		// Search Agent: the collected-info list is revealed under the "Use Search Agent" toggle and
		// aligns with the toggle's label rather than the card edge.
		searchAgentDisclosure: {
			marginLeft: TOGGLE_LABEL_OFFSET,
			marginTop: theme.spacing(3),
		},
		// Sized to the intro sentence: max-content makes the wrapper as wide as the intro on one
		// line, and the checklist stretches to that width (the grid default), so the Select All
		// divider ends where the intro ends. max-width lets the intro wrap on a narrow card rather
		// than overflow it. A fixed max-width or nowrap here would defeat either half of that.
		searchAgentCaptureFields: {
			display: "grid",
			gap: theme.spacing(3),
			width: "max-content",
			maxWidth: "100%",
		},
		searchAgentCaptureIntro: {
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		selectAllRow: {
			borderBottom: `1px solid ${theme.palette.custom.borderCard}`,
			paddingBottom: theme.spacing(2),
			marginBottom: theme.spacing(2),
		},
		searchAgentCaptureList: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2),
		},
		// Zeroes MUI's -11px root margin and the checkbox's 9px padding so the box sits flush with
		// the intro text and the 16px gaps above are the real distance between rows.
		searchAgentCaptureOption: {
			alignItems: "center",
			marginLeft: 0,
			marginRight: 0,
			"& .MuiCheckbox-root": {
				padding: 0,
			},
			"& .MuiFormControlLabel-label": {
				marginLeft: theme.spacing(2),
				fontSize: 16,
				lineHeight: "24px",
				fontWeight: 400,
				color: theme.palette.custom.textPrimary,
			},
		},
		// Select All is styled through the same label slot as the rows below it, so it inherits their
		// 16px checkbox-to-label gap. Passing a Typography element as the label would bypass that slot:
		// MUI v5 only wraps string labels in the MuiFormControlLabel-label element. Declared after
		// searchAgentCaptureOption so the bold weight wins over that rule's 400.
		selectAllOption: {
			"& .MuiFormControlLabel-label": {
				fontWeight: "bold",
			},
		},
		// Bold label for a toggle/checkbox heading.
		settingTitle: {
			fontWeight: "bold",
		},
		// Beta tag beside a section heading.
		betaTag: {
			marginLeft: theme.spacing(1),
		},
		nlsChip: {
			height: 28,
			backgroundColor: theme.palette.primary.faded,
			borderRadius: 16,
			padding: theme.spacing(0.5, 1.5),
			gap: theme.spacing(1),
			display: "inline-flex",
			alignItems: "center",
			"& .MuiChip-label": {
				fontSize: 13,
				lineHeight: "20px",
				fontFamily: theme.typography.fontFamily,
				fontWeight: 400,
				color: theme.palette.text.primary,
				padding: 0,
			},
			"& .MuiChip-icon": {
				margin: 0,
				width: 14,
				height: 14,
				color: theme.palette.primary.main,
			},
		},
	} satisfies Record<string, CSSObject>)
}
